import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Trash, Printer, ArrowLeft, Truck, Receipt, LockSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { PageHeader, Th, Td, Tag, Confirm } from "@/components/common";
import { api, money, fmtDate, fmtDateTime, L, errMsg, qty } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";
import ShipmentDialog from "@/components/ShipmentDialog";
import { OrderItems, ShipmentList } from "@/components/OrderShipments";
import SendInvoiceDialog from "@/components/SendInvoiceDialog";
import PayLinkDialog from "@/components/PayLinkDialog";
import Attachments from "@/components/Attachments";
import { CreditCard } from "@phosphor-icons/react";

export default function DocDetail({ mode }) {
  const { id } = useParams();
  const nav = useNavigate();
  const isOrder = mode === "order";
  const base = isOrder ? "/siparisler" : "/faturalar";
  const ep = isOrder ? "/orders" : "/invoices";
  const [d, reloadDoc] = useLoad(() => api.get(`${ep}/${id}`).then((r) => r.data), [id, mode]);
  const [ships, reloadShips] = useLoad(() => (isOrder ? api.get(`/orders/${id}/shipments`).then((r) => r.data) : Promise.resolve([])), [id, mode]);
  const [shipOpen, setShipOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [acc] = useLoad(() => (isOrder ? Promise.resolve({}) : api.get(`/invoices/${id}`).then((r) => api.get(`/accounts/${r.data.account_id}`).then((a) => a.data))), [id, mode]);
  const reload = () => { reloadDoc(); reloadShips(); };

  const act = async (fn, ok) => {
    try {
      const r = await fn();
      toast.success(ok);
      return r;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const approve = async () => { if (await act(() => api.post(`/orders/${id}/approve`), "Sipariş onaylandı, sevke hazır")) reload(); };
  const invoiceIt = async () => {
    const r = await act(() => api.post(`/orders/${id}/invoice`), "Sevk edilen miktar faturalandı");
    if (r) nav(`/faturalar/${r.data.id}`);
  };
  const closeIt = async () => { if (await act(() => api.post(`/orders/${id}/close`), "Siparişin kalanı kapatıldı")) reload(); };
  const cancel = async () => { if (await act(() => api.post(`/orders/${id}/cancel`), "Sipariş iptal edildi")) reload(); };
  const del = async () => { if (await act(() => api.delete(`${ep}/${id}`), "Silindi")) nav(base); };

  if (!d || !ships) return <div className="label-eyebrow">Yükleniyor…</div>;
  const word = isOrder ? "Siparişi" : "Faturası";
  const cur = d.currency;
  const sale = d.kind === "sale";
  const open = ["approved", "partial"].includes(d.status);
  const uninvoiced = ships.filter((s) => !s.invoice_id).length;
  const canDelete = !isOrder || (ships.length === 0 && !(d.invoices || []).length && d.status !== "invoiced");
  return (
    <div>
      <Link to={base} className="no-print mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="docdetail-back-link"><ArrowLeft size={14} /> {isOrder ? "Siparişler" : "Faturalar"}</Link>
      <PageHeader eyebrow={`${L.kind[d.kind]} ${word}`} title={d.no} testid="docdetail-no">
        {isOrder && d.status === "pending" && (<>
          <Confirm title="Sipariş onaylansın mı?" description="Onaylanan sipariş parça parça sevk edilebilir; stok sevk anında güncellenir." onConfirm={approve} testid="order-approve-confirm"
            trigger={<Button className="rounded-[4px]" data-testid="order-approve-button"><Check /> Onayla</Button>} />
          <Confirm title="Sipariş iptal edilsin mi?" description="İptal edilen sipariş sevk edilemez." onConfirm={cancel} testid="order-cancel-confirm"
            trigger={<Button variant="outline" className="rounded-[4px]" data-testid="order-cancel-button"><X /> İptal Et</Button>} />
        </>)}
        {isOrder && open && (<>
          <Button className="rounded-[4px]" onClick={() => setShipOpen(true)} data-testid="order-ship-button"><Truck /> {sale ? "Sevk Et" : "Mal Kabul"}</Button>
          {d.status === "partial" && (
            <Confirm title="Kalan miktar kapatılsın mı?" description="Sevk edilmeyen miktarlar siparişten düşülür ve sipariş kapanır." onConfirm={closeIt} testid="order-close-confirm"
              trigger={<Button variant="outline" className="rounded-[4px]" data-testid="order-close-button"><LockSimple /> Kalanı Kapat</Button>} />
          )}
          {d.status === "approved" && (
            <Confirm title="Sipariş iptal edilsin mi?" description="İptal edilen sipariş sevk edilemez." onConfirm={cancel} testid="order-cancel-confirm"
              trigger={<Button variant="outline" className="rounded-[4px]" data-testid="order-cancel-button"><X /> İptal Et</Button>} />
          )}
        </>)}
        {isOrder && uninvoiced > 0 && (
          <Confirm title="Sevk edilen miktar faturalansın mı?" description={`${uninvoiced} faturalanmamış sevk tek faturada birleştirilecek; cari bakiye güncellenecek.`} onConfirm={invoiceIt} testid="order-invoice-confirm"
            trigger={<Button variant="outline" className="rounded-[4px] border-emerald-600 text-emerald-700" data-testid="order-invoice-button"><Receipt /> Sevk Edileni Faturala</Button>} />
        )}
        <Button variant="outline" className="rounded-[4px]" onClick={() => window.print()} data-testid="docdetail-print-button"><Printer /> Yazdır</Button>
        {!isOrder && (
          <>
            <Button variant="outline" className="rounded-[4px] border-teal-600 text-teal-700" onClick={() => window.open(`/fatura/${id}?print=1`, "_blank")} data-testid="docdetail-invoice-doc-button"><Receipt /> Fatura Belgesi</Button>
            <Button variant="outline" className="rounded-[4px]" onClick={() => setSendOpen(true)} data-testid="docdetail-send-button"><PaperPlaneTilt /> Gönder</Button>
            {sale && <Button variant="outline" className="rounded-[4px] border-orange-400 text-orange-600" onClick={() => setPayOpen(true)} data-testid="docdetail-paylink-button"><CreditCard /> Ödeme Linki</Button>}
          </>
        )}
        {canDelete && (
          <Confirm title="Silinsin mi?" description={isOrder ? "Sipariş kalıcı olarak silinecek." : "Fatura silinecek; ilgili stok ve cari hareketleri geri alınacak."} onConfirm={del} testid="docdetail-delete-confirm"
            trigger={<Button variant="ghost" size="icon" className="text-red-600" data-testid="docdetail-delete-button"><Trash /></Button>} />
        )}
      </PageHeader>

      <div className="panel p-6 sm:p-10" data-testid="docdetail-document">
        <div className="flex flex-col justify-between gap-6 border-b border-border pb-6 sm:flex-row">
          <div>
            <div className="label-eyebrow">{d.kind === "sale" ? "Müşteri" : "Tedarikçi"}</div>
            <Link to={`/cariler/${d.account_id}`} className="mt-1 block font-display text-2xl font-bold hover:text-primary" data-testid="docdetail-account-link">{d.account_name}</Link>
            {d.notes && <p className="mt-2 max-w-md text-sm text-muted-foreground">{d.notes}</p>}
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <span className="text-muted-foreground">Tarih</span><span className="font-num">{fmtDate(d.date)}</span>
            <span className="text-muted-foreground">Vade</span><span className="font-num">{fmtDate(d.due_date)}</span>
            <span className="text-muted-foreground">Para Birimi</span><span className="font-num">{cur}{cur !== "TRY" && ` · Kur ${d.rate}`}</span>
            {isOrder ? (<>
              <span className="text-muted-foreground">Durum</span><span><Tag tone={d.status} testid="docdetail-status">{L.status[d.status]}</Tag></span>
              {(d.invoices || []).length > 0 && (<><span className="text-muted-foreground">Faturalar</span><span className="flex flex-wrap gap-2">{d.invoices.map((iv) => <Link key={iv.id} className="font-num text-primary hover:underline" to={`/faturalar/${iv.id}`} data-testid={`docdetail-invoice-link-${iv.no}`}>{iv.no}</Link>)}</span></>)}
              {d.invoice_id && (<><span className="text-muted-foreground">Fatura</span><Link className="font-num text-primary hover:underline" to={`/faturalar/${d.invoice_id}`} data-testid="docdetail-invoice-link">{d.invoice_no}</Link></>)}
            </>) : d.order_id && (<>
              <span className="text-muted-foreground">Sipariş</span><Link className="font-num text-primary hover:underline" to={`/siparisler/${d.order_id}`} data-testid="docdetail-order-link">{d.order_no}</Link>
              {d.shipment_nos && (<><span className="text-muted-foreground">Sevkler</span><span className="font-num text-xs">{d.shipment_nos.join(", ")}</span></>)}
            </>)}
            {!isOrder && d.last_sent_at && (<>
              <span className="text-muted-foreground">Son Gönderim</span>
              <span className="text-xs" data-testid="docdetail-last-sent">
                {fmtDateTime(d.last_sent_at)} · {{ email: "E-posta", whatsapp: "WhatsApp", reminder: "Hatırlatma" }[d.last_sent_channel] || d.last_sent_channel} · {d.last_sent_to || "—"}
              </span>
            </>)}
          </div>
        </div>
        {isOrder ? <OrderItems d={d} /> : (
        <div className="overflow-x-auto">
          <table className="mt-6 w-full" data-testid="docdetail-items">
            <thead><tr><Th>Kod</Th><Th>Ürün</Th><Th right>Miktar</Th><Th right>Birim Fiyat</Th><Th right>İsk.</Th><Th right>KDV</Th><Th right>Tutar</Th></tr></thead>
            <tbody>
              {d.items.map((it, i) => (
                <tr key={i}>
                  <Td className="font-num text-xs text-muted-foreground">{it.code}</Td>
                  <Td>{it.name}</Td>
                  <Td right>{qty(it.qty)} <span className="text-xs text-muted-foreground">{it.unit}</span></Td>
                  <Td right>{money(it.price, cur)}</Td>
                  <Td right>{it.discount ? `%${it.discount}` : "—"}</Td>
                  <Td right>%{it.vat_rate}</Td>
                  <Td right className="font-semibold">{money(it.total, cur)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        <div className="ml-auto mt-6 w-full max-w-xs font-num text-sm">
          <div className="flex justify-between py-1"><span className="text-muted-foreground">Ara Toplam</span><span>{money(d.subtotal, cur)}</span></div>
          <div className="flex justify-between py-1"><span className="text-muted-foreground">KDV</span><span>{money(d.vat_total, cur)}</span></div>
          <div className="mt-2 flex justify-between border-t border-border pt-3 text-lg font-semibold"><span>Genel Toplam</span><span data-testid="docdetail-total">{money(d.total, cur)}</span></div>
          {cur !== "TRY" && <div className="mt-1 text-right text-xs text-muted-foreground">TL karşılığı: {money(d.total_try)}</div>}
        </div>
      </div>
      {isOrder && <ShipmentList order={d} shipments={ships} onChanged={reload} />}
      {isOrder && <ShipmentDialog open={shipOpen} onOpenChange={setShipOpen} order={d} onSaved={reload} />}
      {!isOrder && <SendInvoiceDialog open={sendOpen} onOpenChange={setSendOpen} invoice={d} account={acc || {}} onSent={reload} />}
      {!isOrder && sale && <PayLinkDialog open={payOpen} onOpenChange={setPayOpen} invoice={d} onPaid={reload} />}
      {!isOrder && <div className="mt-6"><Attachments entityType="invoice" entityId={d.id} /></div>}
    </div>
  );
}
